import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrap, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatMoney, formatDate, statusTone } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CircleDollarSign,
  Megaphone,
  ChevronRight,
  TrendingUp,
  UserRound,
  CalendarDays,
  ShieldAlert,
  Download,
  Camera,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import { guardianCopyFor, localizedGuardianValue } from "./guardianCopy";
import { levelDisplayName } from "@/lib/levelLabels";
import { useLookupValues } from "@/lib/platformConfig";
import { DatePicker } from "@/components/ui/calendar";

const EMPTY_REPORT_RANGE = { start_date: "", end_date: "" };

const isReportRangeValid = (range) =>
  Boolean(
    range.start_date && range.end_date && range.start_date <= range.end_date,
  );

const StatusBadge = ({ status, labelByValue }) => {
  const { t } = useI18n();
  const map = {
    present: {
      bg: "bg-[var(--success-bg)]",
      fg: "text-[var(--success)]",
      key: "present",
    },
    absent: {
      bg: "bg-[var(--error-bg)]",
      fg: "text-[var(--error)]",
      key: "absent",
    },
    late: {
      bg: "bg-[var(--warning-bg)]",
      fg: "text-[var(--warning)]",
      key: "late",
    },
    half_day: {
      bg: "bg-[var(--warning-bg)]",
      fg: "text-[var(--warning)]",
      key: "half_day",
    },
    excused: {
      bg: "bg-[var(--info-bg)]",
      fg: "text-[var(--info)]",
      key: "excused",
    },
    holiday: {
      bg: "bg-[var(--info-bg)]",
      fg: "text-[var(--info)]",
      key: "holiday",
    },
  };
  const m = map[status] || {
    bg: "bg-[var(--subtle)]",
    fg: "text-[var(--text-secondary)]",
    key: "notMarked",
  };
  const known = map[status];
  const label = known
    ? t(`guardian.${m.key}`)
    : labelByValue?.[status] || status || t("guardian.notMarked");
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${m.bg} ${m.fg}`}
    >
      {label}
    </span>
  );
};

export default function GuardianHome() {
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const attendanceLookup = useLookupValues("attendance_statuses");
  const attendanceRows = attendanceLookup.data?.values || [];
  const attendanceOrder = attendanceRows.length
    ? attendanceRows.map((row) => row.value)
    : [];
  const attendanceLabels = Object.fromEntries(
    attendanceRows.map((row) => [row.value, row.label])
  );
  const [profileStudentId, setProfileStudentId] = useState(null);
  const [reportDownloadingId, setReportDownloadingId] = useState(null);
  const [reportRanges, setReportRanges] = useState({});
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["guardian-home"],
    queryFn: async () => unwrap(await api.get("/guardian/home")),
  });
  const {
    data: emergencyAlerts,
    isLoading: alertsLoading,
    isError: alertsError,
    error: alertsErrorDetails,
  } = useQuery({
    queryKey: ["guardian-emergency-alerts"],
    queryFn: async () =>
      unwrap(await api.get("/panic-alerts", { params: { status: "open" } })),
  });
  const { data: galleryData } = useQuery({
    queryKey: ["guardian-home-gallery"],
    queryFn: async () => unwrap(await api.get("/gallery/parent")),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-[var(--subtle)]">
        <CardContent className="py-10 text-center text-sm text-[var(--error)]">
          {errMsg(error)}
        </CardContent>
      </Card>
    );
  }

  const wards = data?.wards || [];
  const dues = data?.dues || [];
  const circulars = data?.unread_circulars || [];
  const totalDues = dues.reduce((a, i) => a + (i.balance_paise || 0), 0);
  const recentMedia = (() => {
    const albums = Array.isArray(galleryData) ? galleryData : [];
    const flat = [];
    for (const album of albums) {
      const items = Array.isArray(album?.media) ? album.media : [];
      for (const m of items) {
        flat.push({ ...m, _albumTitle: album.title });
      }
    }
    flat.sort((a, b) => {
      const ta = a.taken_at || a.captured_at || a.created_at || "";
      const tb = b.taken_at || b.captured_at || b.created_at || "";
      return (tb || "").localeCompare(ta || "");
    });
    return flat.slice(0, 6);
  })();
  const albumCount = Array.isArray(galleryData) ? galleryData.length : 0;

  const updateReportRange = (studentId, field, value) => {
    setReportRanges((ranges) => ({
      ...ranges,
      [studentId]: {
        ...(ranges[studentId] || EMPTY_REPORT_RANGE),
        [field]: value,
      },
    }));
  };

  const downloadReport = async (student, reportRange) => {
    if (!isReportRangeValid(reportRange)) return;
    setReportDownloadingId(student.id);
    try {
      const response = await api.get(`/eyfs/termly-report.pdf/${student.id}`, {
        params: { lang: language, ...reportRange },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `eyfs-termly-${student.admission_no || student.id}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("nursery.reportDownloadedToast"));
    } catch (e) {
      toast.error(t("nursery.reportDownloadError"));
    } finally {
      setReportDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="guardian-home">
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-semibold">
          {t("guardian.today")}
        </div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] mt-1">
          {wards.length === 1
            ? t("guardian.yourChild")
            : t("guardian.yourWards", { count: wards.length })}
        </h1>
      </div>

      {/* Wards list with today's attendance */}
      <div className="space-y-3 stagger" data-testid="wards-list">
        {wards.map((w) => (
          <Card
            key={w.student.id}
            className="lift-card overflow-hidden border-[var(--subtle)]"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-full bg-[var(--brand)] text-white flex items-center justify-center font-display font-bold">
                      {w.student.first_name?.[0]}
                    </div>
                    <div>
                      <div
                        className="font-semibold text-[var(--text-primary)]"
                        data-testid={`ward-${w.student.admission_no}`}
                      >
                        {w.student.first_name} {w.student.last_name}
                      </div>
                      <div className="text-xs text-[var(--text-muted)]">
                        {levelDisplayName(w.grade, language)} · {copy.section} {w.section?.name} ·{" "}
                        {copy.roll} #{w.student.admission_no}
                      </div>
                    </div>
                  </div>
                </div>
                <StatusBadge status={w.today_status} labelByValue={attendanceLabels} />
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-md bg-[var(--bg)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />{" "}
                    {t("guardian.attendance30d")}
                  </div>
                  <div className="font-display text-xl font-bold text-[var(--brand)] mt-1">
                    {w.attendance_pct_30d}%
                  </div>
                </div>
                <div className="rounded-md bg-[var(--bg)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold flex items-center gap-1">
                    <CircleDollarSign className="h-3 w-3" />{" "}
                    {t("guardian.dues")}
                  </div>
                  <div className="font-mono-num text-xl font-bold text-[var(--accent)] mt-1">
                    {formatMoney(w.dues_paise)}
                  </div>
                </div>
              </div>
              <AttendanceBreakdown summary={w.attendance_30d} attendanceOrder={attendanceOrder} attendanceLabels={attendanceLabels} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setProfileStudentId(w.student.id)}
                  data-testid={`view-profile-${w.student.admission_no}`}
                >
                  <UserRound className="h-3.5 w-3.5 mr-1.5" />{" "}
                  {t("guardian.view")}
                </Button>
                <ReportDownloadControls
                  student={w.student}
                  reportRange={reportRanges[w.student.id] || EMPTY_REPORT_RANGE}
                  reportDownloading={reportDownloadingId === w.student.id}
                  onRangeChange={updateReportRange}
                  onDownload={downloadReport}
                  t={t}
                />
              </div>
            </CardContent>
          </Card>
        ))}
        {wards.length === 0 && (
          <Card className="border-[var(--subtle)]">
            <CardContent className="py-10 text-center text-[var(--text-secondary)]">
              {t("guardian.noWards")}
            </CardContent>
          </Card>
        )}
      </div>

      <EmergencyAlerts
        alerts={emergencyAlerts}
        isLoading={alertsLoading}
        isError={alertsError}
        error={alertsErrorDetails}
        copy={copy.emergency}
      />

      {/* Activity Photos & Videos (R-15 — high priority) */}
      <Card
        className="border-[var(--subtle)] lift-card overflow-hidden"
        data-testid="guardian-home-gallery-card"
      >
        <div className="bg-gradient-to-br from-purple-600 via-fuchsia-500 to-pink-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Camera className="h-4 w-4" />
            <CardTitle className="text-sm font-semibold text-white">
              {t("guardian.activityPhotosTitle")}
            </CardTitle>
            {albumCount > 0 && (
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 ml-1">
                {t("guardian.albumsCount", { count: albumCount })}
              </Badge>
            )}
          </div>
          <Link to="/parent/gallery" data-testid="link-gallery">
            <Button
              variant="secondary"
              size="sm"
              className="text-xs bg-white/20 hover:bg-white/30 text-white border-0"
            >
              {t("guardian.activityPhotosViewAll")}{" "}
              <ChevronRight className="h-3 w-3 ml-0.5" />
            </Button>
          </Link>
        </div>
        <CardContent className="pt-3 pb-4">
          {recentMedia.length === 0 ? (
            <div className="text-sm text-[var(--text-secondary)]">
              {t("guardian.activityPhotosEmpty")}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {recentMedia.map((m, idx) => {
                const isVideo = m.media_type === "video" || !!m.video_url;
                const src = m.thumbnail_url || m.url || m.image_url;
                return (
                  <Link
                    to="/parent/gallery"
                    key={m.id || `${m._albumTitle}-${idx}`}
                    className="group relative aspect-square overflow-hidden rounded-md bg-[var(--subtle)]"
                    data-testid={`guardian-home-gallery-tile-${idx}`}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={m.caption || m._albumTitle || ""}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-[var(--text-muted)]">
                        <Camera className="h-5 w-5" />
                      </div>
                    )}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="h-5 w-5 text-white fill-white" />
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fees & Circulars quick sections */}
      <div className="grid gap-4">
        <Card className="border-[var(--subtle)] lift-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CircleDollarSign className="h-4 w-4 text-[var(--accent)]" />{" "}
                {t("guardian.feesDue")}
              </CardTitle>
              <Link to="/parent/fees" data-testid="link-fees">
                <Button variant="ghost" size="sm" className="text-xs">
                  {t("guardian.view")}{" "}
                  <ChevronRight className="h-3 w-3 ml-0.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            {dues.length === 0 ? (
              <div className="text-sm text-[var(--text-secondary)]">
                {t("guardian.allCaughtUp")}
              </div>
            ) : (
              <div>
                <div className="font-mono-num text-3xl font-bold text-[var(--accent)]">
                  {formatMoney(totalDues)}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  {t("guardian.acrossInvoices", {
                    count: dues.length,
                    plural: dues.length > 1 ? "s" : "",
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)] lift-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-[var(--accent)]" />{" "}
                {t("guardian.unreadCirculars")}
                {circulars.length > 0 && (
                  <Badge className="bg-[var(--accent)] hover:bg-[var(--accent)] ml-1">
                    {circulars.length}
                  </Badge>
                )}
              </CardTitle>
              <Link to="/parent/circulars" data-testid="link-circulars">
                <Button variant="ghost" size="sm" className="text-xs">
                  {t("guardian.viewAll")}{" "}
                  <ChevronRight className="h-3 w-3 ml-0.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            {circulars.length === 0 ? (
              <div className="text-sm text-[var(--text-secondary)]">
                {t("guardian.noNewCirculars")}
              </div>
            ) : (
              <ul className="space-y-2">
                {circulars.slice(0, 3).map((c) => (
                  <li
                    key={c.id}
                    className="border-l-2 pl-3"
                    style={{ borderLeftColor: c.kind === "emergency" ? "var(--error)" : "var(--warning)" }}
                  >
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{c.title}</div>
                    {c.description && (
                      <div className="text-xs text-[var(--text-secondary)]">{c.description}</div>
                    )}
                    <div className="text-xs text-[var(--text-muted)]">{formatDate(c.publish_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <GuardianProfileDialog
        studentId={profileStudentId}
        onClose={() => setProfileStudentId(null)}
      />
    </div>
  );
}

function ReportDownloadControls({
  student,
  reportRange,
  reportDownloading,
  onRangeChange,
  onDownload,
  t,
}) {
  const rangeValid = isReportRangeValid(reportRange);

  return (
    <div className="space-y-2">
      <fieldset
        className="grid grid-cols-[1fr_auto_1fr] items-center gap-1"
        data-testid={`eyfs-report-period-${student.admission_no}`}
      >
        <legend className="sr-only">{t("finance.period")}</legend>
        <DatePicker
          ariaLabel={t("finance.period")}
          value={reportRange.start_date}
          max={reportRange.end_date || undefined}
          onChange={(date) => onRangeChange(student.id, "start_date", date)}
          testId={`eyfs-report-start-${student.admission_no}`}
        />
        <span aria-hidden="true" className="text-xs text-[var(--text-muted)]">
          →
        </span>
        <DatePicker
          ariaLabel={t("finance.period")}
          value={reportRange.end_date}
          min={reportRange.start_date || undefined}
          onChange={(date) => onRangeChange(student.id, "end_date", date)}
          testId={`eyfs-report-end-${student.admission_no}`}
        />
      </fieldset>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => onDownload(student, reportRange)}
        disabled={!rangeValid || reportDownloading}
        data-testid={`download-eyfs-report-${student.admission_no}`}
        title={t("nursery.downloadTermlyReport")}
      >
        <Download className="h-3.5 w-3.5 mr-1.5" />{" "}
        {t("nursery.downloadTermlyReport")}
      </Button>
    </div>
  );
}

function EmergencyAlerts({ alerts, isLoading, isError, error, copy }) {
  if (isLoading)
    return (
      <Skeleton
        className="h-28 w-full"
        data-testid="guardian-emergency-alerts-loading"
      />
    );
  if (isError)
    return (
      <Card
        className="border-[var(--error)]/30"
        data-testid="guardian-emergency-alerts-error"
      >
        <CardContent className="py-4 text-sm text-[var(--error)]">
          {errMsg(error)}
        </CardContent>
      </Card>
    );
  if (!alerts?.length)
    return (
      <Card
        className="border-[var(--subtle)]"
        data-testid="guardian-emergency-alerts-empty"
      >
        <CardContent className="py-4 text-sm text-[var(--text-muted)]">
          {copy.empty}
        </CardContent>
      </Card>
    );

  return (
    <Card
      className="border-[var(--error)]/30 bg-[var(--error-bg)]"
      data-testid="guardian-emergency-alerts"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-[var(--error)] flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> {copy.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="rounded-md border border-[var(--error)]/20 bg-white/70 p-3"
            data-testid={`guardian-emergency-alert-${alert.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-widest text-[var(--error)]">
                  {copy.active}
                </div>
                <div className="font-semibold text-[var(--text-primary)] mt-1">
                  {alert.student_name}
                </div>
              </div>
              <ShieldAlert className="h-4 w-4 flex-shrink-0 text-[var(--error)]" />
            </div>
            <div className="text-sm text-[var(--text-primary)] mt-2">
              {alert.message}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--text-secondary)]">
              {alert.location && (
                <span>
                  {copy.location}: {alert.location}
                </span>
              )}
              {alert.raised_at && (
                <span>
                  {copy.raised}: {formatDate(alert.raised_at)}
                </span>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AttendanceBreakdown({ summary, attendanceOrder, attendanceLabels }) {
  const { t } = useI18n();
  const counts = summary?.counts || {};
  const statuses = (attendanceOrder || []).filter((status) => status !== "holiday");
  const visibleStatuses = statuses.length
    ? statuses
    : Object.keys(counts).filter((status) => status !== "holiday");
  const marked = summary?.total_marked || 0;

  if (!marked) {
    return (
      <div
        className="mt-3 text-xs text-[var(--text-muted)]"
        data-testid="attendance-breakdown-empty"
      >
        {t("guardian.notMarked")}
      </div>
    );
  }

  return (
    <div
      className="mt-3 flex flex-wrap gap-1.5"
      data-testid="attendance-breakdown"
    >
      {visibleStatuses
        .filter((status) => counts[status] > 0)
        .map((status) => (
          <div
            key={status}
            className="inline-flex items-center gap-1 rounded bg-[var(--bg)] px-1.5 py-1"
          >
            <StatusBadge status={status} labelByValue={attendanceLabels} />
            <span className="font-mono-num text-xs font-semibold text-[var(--text-secondary)]">
              {counts[status]}
            </span>
          </div>
        ))}
      {counts.holiday > 0 && (
        <div className="inline-flex items-center gap-1 rounded bg-[var(--bg)] px-1.5 py-1 text-xs text-[var(--text-muted)]">
          <CalendarDays className="h-3 w-3" /> {counts.holiday}
        </div>
      )}
    </div>
  );
}

function GuardianProfileDialog({ studentId, onClose }) {
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const [failedPhotoId, setFailedPhotoId] = useState(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["guardian-student-profile", studentId],
    queryFn: async () =>
      unwrap(await api.get(`/guardian/students/${studentId}`)),
    enabled: !!studentId,
  });

  return (
    <Dialog open={!!studentId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md"
        closeLabel={copy.close}
        data-testid="guardian-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            {data
              ? `${data.first_name || ""} ${data.last_name || ""}`.trim()
              : copy.profile.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {copy.profile.description}
          </DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-40 w-full" />}
        {isError && (
          <div className="text-sm text-[var(--error)]">{errMsg(error)}</div>
        )}
        {data && !isLoading && !isError && (
          <div className="space-y-4" data-testid="guardian-profile-details">
            {data.photo_url && failedPhotoId !== data.id ? (
              <img
                src={data.photo_url}
                alt={`${data.first_name || ""} ${data.last_name || ""}`.trim()}
                onError={() => setFailedPhotoId(data.id)}
                className="h-20 w-20 rounded-md object-cover border border-[var(--subtle)]"
              />
            ) : (
              <div className="h-20 w-20 rounded-md bg-[var(--brand)] text-white flex items-center justify-center font-display font-bold text-3xl">
                {data.first_name?.[0] || "?"}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.admissionNo}
                </div>
                <div className="font-mono-num mt-1">
                  {data.admission_no || "-"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.status}
                </div>
                <div
                  className="mt-1 capitalize text-[var(--text-primary)]"
                  data-testid="guardian-profile-status"
                >
                  {localizedGuardianValue(language, "statuses", data.status)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.dateOfBirth}
                </div>
                <div className="mt-1">{formatDate(data.date_of_birth)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.gender}
                </div>
                <div className="capitalize mt-1">
                  {localizedGuardianValue(language, "genders", data.gender)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.admissionDate}
                </div>
                <div className="mt-1">{formatDate(data.admission_date)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                  {copy.profile.previousSchool}
                </div>
                <div className="mt-1">{data.previous_school || "-"}</div>
              </div>
            </div>
          </div>
        )}
        <div className="sr-only">{t("guardian.view")}</div>
      </DialogContent>
    </Dialog>
  );
}
